import React, { useEffect, useState, useMemo } from 'react';
import { Stack, Box } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { ContentLayout } from 'components/Layout';
import { MyAdsKanban } from '../../components/MyAds/MyAdsKanban';
import { MyAdsListView } from '../../components/MyAds/MyAdsListView';
import { AssignedUserFilter } from '../../components/MyAds/AssignedUserFilter';
import { BreadCrumbs } from 'components/Breadcrumbs';
import { DetailViewTabList } from 'components/TabList';
import { paths } from 'routes';
import { useGetAll } from '../../api/getAll';
import { useAuth } from 'lib/auth';
import { usePageSize } from 'hook/usePageSize';

const KANBAN_VIEW = 'kanban';
const LIST_VIEW = 'list';

const KANBAN_FILTERS = {
  includeInactive: true,
  includeExpired: true,
  sortBy: 'dateCreated',
  excludeOutcome: true,
};

const LIST_FILTERS = {
  includeInactive: false,
  includeExpired: false,
  sortBy: 'dateCreated',
};

export const List = () => {
  const { account, user } = useAuth();
  const isBuyer = !account.super_admin && user?.Company?.Type === 'buyer';

  const [value, setValue] = useState(KANBAN_VIEW);
  const [page, setPage] = useState(1);
  const [pageSize] = usePageSize();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(isBuyer ? LIST_FILTERS : KANBAN_FILTERS);
  const [assignedUser, setAssignedUser] = useState(
    user?.user_id ? { user_id: user.user_id, full_name: `${user.first_name} ${user.last_name}` } : null
  );

  const {
    data: { jobAds, total },
    isFetching,
    refetch,
  } = useGetAll({
    search,
    ...filters,
    assignedUserID: assignedUser?.user_id ?? null,
    page: page - 1,
    ...((isBuyer || value === LIST_VIEW) && { limit: pageSize }),
    queryOptions: {},
  });

  const displayTotal = total;

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setPage(1); // Reset page when changing tabs
    setFilters(newValue === KANBAN_VIEW ? KANBAN_FILTERS : LIST_FILTERS);
  };

  const debouncedSearchChange = useDebouncedCallback((val) => setSearch(val), 300);
  const handleSearchChange = ({ target: { value: val } }) => {
    debouncedSearchChange(val);
  };

  const handlePageChange = (_, newPage) => setPage(newPage);

  const handleAssignedUserChange = (newValue) => {
    setAssignedUser(newValue);
    setPage(1);
  };

  const assignedUserFilter = (
    <AssignedUserFilter
      companyID={user?.Company?.company_id}
      value={assignedUser}
      onChange={handleAssignedUserChange}
    />
  );
  
  useEffect(() => {
    refetch();
  }, [page, pageSize, filters, search, assignedUser, refetch]);

  return (
    <ContentLayout>
      <Stack height={1}>
        <Stack>
          <BreadCrumbs
            items={[{ name: '', to: paths.myJobAds }, { name: `My Job Advertisements${displayTotal > 0 ? ` (${displayTotal})` : ''}` }]}
          />
        </Stack>
        <Box sx={{ width: '100%' }}>
          {isBuyer ? (
            <MyAdsListView
              jobAds={jobAds}
              total={total}
              isFetching={isFetching}
              page={page}
              handleSearchChange={handleSearchChange}
              handlePageChange={handlePageChange}
              refetch={refetch}
              filters={assignedUserFilter}
            />
          ) : (
            <>
              <Box sx={{ height: 16, backgroundColor: 'background.paper' }} />
              <DetailViewTabList
                value={value}
                onChange={handleChange}
                tabs={[
                  { label: 'Kanban', value: KANBAN_VIEW },
                  { label: 'List', value: LIST_VIEW },
                ]}
              />
              {value === KANBAN_VIEW ? (
                <MyAdsKanban
                  jobAds={jobAds}
                  isFetching={isFetching}
                  handleSearchChange={handleSearchChange}
                  refetch={refetch}
                  filters={assignedUserFilter}
                />
              ) : (
                <MyAdsListView
                  jobAds={jobAds}
                  total={total}
                  isFetching={isFetching}
                  page={page}
                  handleSearchChange={handleSearchChange}
                  handlePageChange={handlePageChange}
                  refetch={refetch}
                  filters={assignedUserFilter}
                />
              )}
            </>
          )}
        </Box>
      </Stack>
    </ContentLayout>
  );
};